/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {useLocation} from 'react-router-dom'
import {MenuComponent} from '../../../../../../_metronic/assets/ts/components'
import {ID, KTIcon, QUERIES} from '../../../../../../_metronic/helpers'
import {Confirm} from '../../../../../components/Confirm'
import {MenuModel, getMenuAccess, useAuth} from '../../../../auth'
import {useListView} from '../../core/ListViewProvider'
import {useQueryResponse} from '../../core/QueryResponseProvider'
import {deleteJudol, rejectJudol, setDraftJudol, verifyJudol} from '../../core/_requests'
import {OverlayTooltip} from '../../../../../components/OverlayTooltip'
import {IButtonPermission} from '../../../../../models/IButtonPermission'

type Props = {
  id: ID
  name: string
  permission: IButtonPermission
}

const JudolActionsCell: FC<Props> = ({id, name, permission}) => {
  const {setItemIdForUpdate} = useListView()
  const {query} = useQueryResponse()
  const queryClient = useQueryClient()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showVerifyConfirmation, setShowVerifyConfirmation] = useState(false)
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false)
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false)
  const [showSetDraftConfirmation, setShowSetDraftConfirmation] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const {pathname} = useLocation()
  const {currentMenu} = useAuth()
  const [menuAccess, setMenuAccess] = useState<MenuModel | undefined>(undefined)
  useEffect(() => {
    setMenuAccess(getMenuAccess(currentMenu ?? [], pathname))
  }, [currentMenu])

  /* BEGIN:DELETE */

  const handleCloseDeleteConfirmation = () => setShowDeleteConfirmation(false)
  const handleConfirmDelete = async () => {
    // Perform your action here after confirmation
    setIsLoading(true)
    try {
      await deleteItem.mutateAsync()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleOpenDeleteConfirmation = () => {
    setError(null)
    setShowDeleteConfirmation(true)
  }

  const deleteItem = useMutation(() => deleteJudol(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      // ✅ update detail view directly
      setIsLoading(false)
      handleCloseDeleteConfirmation()
      queryClient.invalidateQueries([`${QUERIES.PUBLIC_USERS_LIST}-${query}`])
    },
    onError: (err: any) => {
      setError(err.message)
      setIsLoading(false)
    },
  })

  /* END:DELETE */

  /* BEGIN:VERIFY */

  const handleCloseVerifyConfirmation = () => setShowVerifyConfirmation(false)
  const handleConfirmVerify = async () => {
    // Perform your action here after confirmation
    setIsLoading(true)
    try {
      await verifyItem.mutateAsync()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleOpenVerifyConfirmation = () => {
    setError(null)
    setShowVerifyConfirmation(true)
  }

  const verifyItem = useMutation(() => verifyJudol(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      // ✅ update detail view directly
      setIsLoading(false)
      handleCloseVerifyConfirmation()
      queryClient.invalidateQueries([`${QUERIES.PUBLIC_USERS_LIST}-${query}`])
    },
    onError: (err: any) => {
      setError(err.message)
      setIsLoading(false)
    },
  })

  /* END:VERIFY */

  /* BEGIN:SUBMIT */

  const handleCloseSubmitConfirmation = () => setShowSubmitConfirmation(false)
  const handleConfirmSubmit = async () => {
    // Perform your action here after confirmation
    setIsLoading(true)
    try {
      await submitItem.mutateAsync()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleOpenSubmitConfirmation = () => {
    setError(null)
    setShowSubmitConfirmation(true)
  }

  const submitItem = useMutation(() => verifyJudol(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      // ✅ update detail view directly
      setIsLoading(false)
      handleCloseSubmitConfirmation()
      queryClient.invalidateQueries([`${QUERIES.PUBLIC_USERS_LIST}-${query}`])
    },
    onError: (err: any) => {
      setError(err.message)
      setIsLoading(false)
    },
  })

  /* END:SUBMIT */

  /* BEGIN:REJECT */

  const handleCloseRejectConfirmation = () => setShowRejectConfirmation(false)
  const handleConfirmReject = async () => {
    // Perform your action here after confirmation
    setIsLoading(true)
    try {
      await rejectItem.mutateAsync()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleOpenRejectConfirmation = () => {
    setError(null)
    setShowRejectConfirmation(true)
  }

  const rejectItem = useMutation(() => rejectJudol(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      // ✅ update detail view directly
      setIsLoading(false)
      handleCloseRejectConfirmation()
      queryClient.invalidateQueries([`${QUERIES.PUBLIC_USERS_LIST}-${query}`])
    },
    onError: (err: any) => {
      setError(err.message)
      setIsLoading(false)
    },
  })

  /* END:REJECT */

  /* BEGIN:SETDRAFT */

  const handleCloseSetDraftConfirmation = () => setShowSetDraftConfirmation(false)
  const handleConfirmSetDraft = async () => {
    // Perform your action here after confirmation
    setIsLoading(true)
    try {
      await setDraftItem.mutateAsync()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleOpenSetDraftConfirmation = () => {
    setError(null)
    setShowSetDraftConfirmation(true)
  }

  const setDraftItem = useMutation(() => setDraftJudol(id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: () => {
      // ✅ update detail view directly
      setIsLoading(false)
      handleCloseSetDraftConfirmation()
      queryClient.invalidateQueries([`${QUERIES.PUBLIC_USERS_LIST}-${query}`])
    },
    onError: (err: any) => {
      setError(err.message)
      setIsLoading(false)
    },
  })

  /* END:SETDRAFT */

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const openEditModal = () => {
    setItemIdForUpdate(id)
  }

  return (
    <>
      {menuAccess?.canCreate && permission?.allowEdit &&
        <OverlayTooltip title="Edit">
          <button className='btn btn-link' onClick={openEditModal}>
            <i className='fa-solid fa-pen-to-square'></i>
          </button>
        </OverlayTooltip>
      }

      {(!(menuAccess?.canCreate) || !permission?.allowEdit) &&
        <OverlayTooltip title="Lihat">
          <button className='btn btn-link' onClick={openEditModal}>
            <i className='fa-solid fa-eye'></i>
          </button>
        </OverlayTooltip>
      }

      {menuAccess?.canCreate && permission?.allowSubmit && (
        <>
          <span className="ms-1 me-2">|</span>
          <OverlayTooltip title="Submit">
            <button className='btn btn-link' onClick={handleOpenSubmitConfirmation}>
              <i className='fa-solid fa-paper-plane'></i>
            </button>
          </OverlayTooltip>
        </>
      )}

      {menuAccess?.canCreate && permission?.allowVerif && (
        <>
          <span className="ms-1 me-2">|</span>
          <OverlayTooltip title="Verifikasi">
            <button className='btn btn-link' onClick={handleOpenVerifyConfirmation}>
              <i className='fa-solid fa-check-to-slot'></i>
            </button>
          </OverlayTooltip>
        </>
      )}

      {menuAccess?.canCreate && permission?.allowReject && (
        <>
          <span className="ms-1 me-2">|</span>
          <OverlayTooltip title='Tolak'>
            <button className='btn btn-link' onClick={handleOpenRejectConfirmation}>
              <i className='fa-solid fa-circle-xmark'></i>
            </button>
          </OverlayTooltip>
        </>
      )}

      {menuAccess?.canDelete && permission?.allowDelete && (
        <>
          <span className='ms-1 me-2'>|</span>
          <OverlayTooltip title='Hapus'>
            <button className='btn btn-link' onClick={handleOpenDeleteConfirmation}>
              <i className='fa-solid fa-trash'></i>
            </button>
          </OverlayTooltip>
        </>
      )}

      {menuAccess?.canCreate && permission?.allowSetDraft && (
        <>
          <span className='ms-1 me-2'>|</span>
          <OverlayTooltip title='Kembalikan jadi Draft'>
            <button className='btn btn-link' onClick={handleOpenSetDraftConfirmation}>
              <i className='fa-solid fa-rotate-left'></i>
            </button>
          </OverlayTooltip>
        </>
      )}
      <Confirm
        show={showDeleteConfirmation}
        handleClose={handleCloseDeleteConfirmation}
        handleConfirm={handleConfirmDelete}
        name={`Hapus ${name}`}
        body={`Apakah anda yakin ingin menghapus ${name}?`}
        isLoading={isLoading}
        error={error}
      />
      <Confirm
        show={showVerifyConfirmation}
        handleClose={handleCloseVerifyConfirmation}
        handleConfirm={handleConfirmVerify}
        name={`Verifikasi ${name}`}
        body={`Apakah anda yakin ingin memverifikasi ${name}?`}
        isLoading={isLoading}
        error={error}
      />
      <Confirm
        show={showSubmitConfirmation}
        handleClose={handleCloseSubmitConfirmation}
        handleConfirm={handleConfirmSubmit}
        name={`Submit ${name}`}
        body={`Apakah anda yakin ingin mengirim ${name}?`}
        isLoading={isLoading}
        error={error}
      />
      <Confirm
        show={showRejectConfirmation}
        handleClose={handleCloseRejectConfirmation}
        handleConfirm={handleConfirmReject}
        name={`Tolak ${name}`}
        body={`Apakah anda yakin ingin menolak ${name}?`}
        isLoading={isLoading}
        error={error}
      />
      <Confirm
        show={showSetDraftConfirmation}
        handleClose={handleCloseSetDraftConfirmation}
        handleConfirm={handleConfirmSetDraft}
        name={`Kembalikan ${name} Jadi Draft`}
        body={`Apakah anda yakin ingin mengembalikan ${name} menjadi draft?`}
        isLoading={isLoading}
        error={error}
      />
      {/* end::Menu */}
    </>
  )
}

export {JudolActionsCell}
